import {
  ArrivalDateVariation,
  HandlingTimeVariation,
} from "@/app/ec/product/[basePath]/vendor-options/arrivalDateAbTest";
import getArrivalDateLabel from "@/app/ec/product/[basePath]/vendor-options/getArrivalDateLabel";
import getStockedMessage, { StockedState } from "@/app/ec/product/[basePath]/vendor-options/getStockedMessage";
import moment from "moment";
import { useMemo } from "react";
import styles from "./stockAndShippingMessage.module.scss";

/**
 * Provides the shipping messages for a vendor, similar to "Stocked. Ships from NC in 6 business days."  Shipping times greater than 30 days are adjusted to say "30+"
 * @TODO [Stocked?] is a placeholder for when we can retrieve the fulfillment policy.
 * @param {VendorOptionState} vendorOption: VendorOptionState,

* @param {string} [stockMessageClassName] A pass through for this element's className prop.
 * @param {string} [businessDaysClassName] A pass through for this element's className prop.
 * @param {string} [longHandlingTimeClassName] A pass through for this element's className prop.
 * @param {string} [className] A pass through for this element's className prop.
 * @param {boolean} showViewOtherOptionsLink If true, will show "View Other Options" link when the long handling time message is shown.
 * @param {number} totalVendors The total number of vendors for use with showViewOtherOptionsLink.  Required if showViewOtherOptionsLink is set to true
 * @param {boolean} [showArrivalDateVariation] If true, also renders the arrival date variation for the AB Tasty test.
 */

function StockAndShippingMessage({
  vendorOption,
  className,
  stockMessageClassName,
  businessDaysClassName,
  longHandlingTimeClassName,
  hideStockAndShippingMessageOnMobile,
  hideBackordered,
  twoDayDeliverySw,
  showArrivalDateVariation = false,
}: StockAndShippingMessageProps) {
  const adjustedShippingTime = useMemo(() => {
    if (vendorOption.isBackordered === true) {
      return null;
    } else if (vendorOption.shippingTime >= 30) {
      return "30+";
    } else {
      return vendorOption.shippingTime;
    }
  }, [vendorOption.isBackordered, vendorOption.shippingTime]);

  stockMessageClassName = stockMessageClassName ? stockMessageClassName : "";
  longHandlingTimeClassName = longHandlingTimeClassName ? longHandlingTimeClassName : "";
  className = className ? className : "";
  businessDaysClassName = businessDaysClassName ? businessDaysClassName : "";

  const formattedArrivalDate = useMemo(() => {
    if (vendorOption.arrivalDate) return moment(vendorOption.arrivalDate).format("dddd, MMM D");
    return vendorOption.preFormattedArrivalDate;
  }, [vendorOption.arrivalDate, vendorOption.preFormattedArrivalDate]);

  const arrivalDateLabel = useMemo(
    () => (showArrivalDateVariation && adjustedShippingTime !== null ? getArrivalDateLabel(vendorOption) : null),
    [showArrivalDateVariation, adjustedShippingTime, vendorOption],
  );

  const stockStateMessage = useMemo(() => {
    const stockState = getStockedMessage(vendorOption);
    if (stockState === StockedState.LONG_HANDLING_TIME) {
      return <span className={`${styles.longHandlingTime} me-1 ${longHandlingTimeClassName}`}>Long Handling Time</span>;
    } else if (!hideBackordered && stockState === StockedState.BACKORDERED) {
      return <span className={`pe-1 ${styles.backordered}`}>Backordered.</span>;
    } else if (stockState === StockedState.STOCKED) {
      return <span className={`me-1 ${stockMessageClassName}`}>Stocked.</span>;
    } else {
      return <></>;
    }
  }, [vendorOption, longHandlingTimeClassName, stockMessageClassName]);

  return (
    <span
      className={`shipment-info ${className} ${
        hideStockAndShippingMessageOnMobile === true ? `hide-stock-and-shipping-message-on-mobile` : ""
      }`}
    >
      {stockStateMessage}

      {adjustedShippingTime === null && (
        <span className="shipment-info-ships-from">Ships from {vendorOption.vendorRegion}.</span>
      )}

      {adjustedShippingTime !== null && (
        <HandlingTimeVariation enabled={arrivalDateLabel !== null}>
          <span className="shipment-info-ships-from">
            Ships from {vendorOption.vendorRegion}
            <span className={vendorOption.twoDayDeliverySw || twoDayDeliverySw ? "hide-business-days" : ""}>
              {" "}
              in{" "}
              <span className={`shipment-info-business-days ${businessDaysClassName}`}>
                {adjustedShippingTime} business day{vendorOption.shippingTime > 1 ? "s" : ""}
              </span>
            </span>
            .
          </span>
        </HandlingTimeVariation>
      )}

      {arrivalDateLabel !== null && (
        <ArrivalDateVariation>
          <span className="shipment-info-ships-from">
            {vendorOption.vendorRegion
              ? `Estimated Delivery from ${vendorOption.vendorRegion}:`
              : "Estimated Delivery:"}{" "}
            <span className={styles.arrivalDate}>{arrivalDateLabel}</span>.
          </span>
        </ArrivalDateVariation>
      )}

      <span className={`shipment-info-arrives-by ${businessDaysClassName}`}>
        Arrives by <span className="arrival-date">{formattedArrivalDate}.</span>
      </span>
    </span>
  );
}

type StockAndShippingMessageProps = {
  vendorOption: VendorOptionState;
  stockMessageClassName?: string;
  businessDaysClassName?: string;
  longHandlingTimeClassName?: string;
  className?: string;
  hideStockAndShippingMessageOnMobile?: boolean;
  hideBackordered?: boolean;
  twoDayDeliverySw?: boolean;
  showArrivalDateVariation?: boolean;
} & (
  | {
      /** If true, will show "View other options" link when the long handling time message is shown. */
      showViewOtherOptionsLink: true;
      /** For use if showViewOtherOptionsLink is enabled.  Required by default */
      totalVendors: number;
    }
  | {
      /** If true, will show "View other options" link when the long handling time message is shown. */
      showViewOtherOptionsLink?: false;
      totalVendors?: number;
    }
);

export default StockAndShippingMessage;
